/**
 * Waveform Visualizer - Shows audio waveform or configured waves
 * Displays waveforms vertically stacked with frequency badges
 */

import { BaseVisualizer, VisualizerConfig, VisualizerMetadata } from '../types';
import { colorHelper } from '../ColorModeHelper';

/**
 * Interface for the processed wave data used in visualizations.
 */
export interface WaveData {
  freq: number;
  amp: number;
  phase: number;
  color: string;
  waveform: string;
  channel: 'left' | 'right' | 'both';
}

/**
 * Helper definition for raw extracted peaks before filtering.
 */
interface RawPeak {
  freq: number;
  amp: number;
  channel: number;
}

export class AudioParser {
  /**
   * Parses raw audio data (interleaved freq, amp, channel) into clean, 
   * isolated wave components suitable for Lissajous visualization.
   * 
   * Optimizations:
   * 1. Frequency Masking: Ignores weak frequencies adjacent to strong ones (spectral leakage).
   * 2. Range Limiting: Focuses on audible spectrum (20Hz - 20kHz).
   * 3. Non-linear Amplitude: Uses square root scaling to make quiet details visible without clipping.
   * 
   * @param audioData - Interleaved array [freq, amp, channel, freq, amp, channel, ...]
   * @returns Array of top 8 distinct WaveData objects.
   */
  public parseAudioData(audioData: number[]): Array<WaveData> {
    const candidates: RawPeak[] = [];
    const len = audioData.length;

    // 1. Extraction Pass
    // Iterate with stride 3. 
    // Optimization: Filter strictly inside the loop to avoid creating objects for noise.
    for (let i = 0; i + 2 < len; i += 3) {
      const freq = audioData[i];
      const amp = audioData[i + 1];
      const channel = audioData[i + 2];

      // Filter: Audible Range (20Hz-20kHz) & Noise Gate (0.005 to catch subtleties)
      if (amp > 0.005 && freq >= 20 && freq <= 20000) {
        candidates.push({ freq, amp, channel });
      }
    }

    // 2. Sort by Amplitude Descending
    // This ensures we process the loudest sounds first.
    candidates.sort((a, b) => b.amp - a.amp);

    // 3. Peak Selection with Masking (The "Clean Visuals" Secret)
    // We select up to 8 peaks. If a new peak is too close in frequency 
    // to an already selected stronger peak, we skip it. 
    // This removes "mud" caused by spectral leakage.
    const selected: RawPeak[] = [];
    const maskWidthPercent = 0.10; // 10% frequency masking (e.g., if 100Hz is picked, ignore 90-110Hz)

    for (const cand of candidates) {
      if (selected.length >= 8) break;

      let isMasked = false;
      for (const existing of selected) {
        // Check frequency proximity
        const diff = Math.abs(cand.freq - existing.freq);
        const threshold = existing.freq * maskWidthPercent;
        
        // Also check if they are the same channel or if we want global masking.
        // For Lissajous, keeping L and R distinct is good, but masking close frequencies
        // generally looks cleaner regardless of channel.
        if (diff < threshold) {
          isMasked = true;
          break;
        }
      }

      if (!isMasked) {
        selected.push(cand);
      }
    }

    // 4. Map to Output Format
    return selected.map((item) => {
      const channelName = item.channel === 1 ? 'left' : item.channel === 2 ? 'right' : 'both';
      
      // Amplitude Scaling:
      // Raw linear amp often looks "spiky". 
      // Math.sqrt compresses dynamic range slightly so you see more structure.
      const scaledAmp = Math.sqrt(item.amp) * 100;

      return {
        freq: parseFloat(item.freq.toFixed(2)), // Clean floats for UI display
        amp: Math.min(scaledAmp, 100), // Cap at 100 purely for safety
        phase: 0, // Lissajous relying on phase drift usually needs state; 0 is safe for pure parser
        color: colorHelper.getChannelColor(channelName),
        waveform: 'sine',
        channel: channelName,
      };
    });
  }
}

export class WaveformVisualizer extends BaseVisualizer {
  private audioParser: AudioParser;

  constructor() {
    super();
    this.audioParser = new AudioParser();
  }

  readonly metadata: VisualizerMetadata = {
    id: 'waveform',
    name: 'Waveform Display',
    description: 'Display individual waveforms or audio spectrum',
    category: 'time',
    defaultZoom: 1,
    supports3D: false,
    supportsAutoRotation: false,
    supportsPanning: false,
    supportsZooming: false,
  };

  render(config: VisualizerConfig): void {
    const { 
      ctx, width, height, waves, speed, time, colorMode = 'wave',
      uploadedAudioData, isUploadedAudioPlaying 
    } = config;

    // Clear canvas
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, width, height);

    // Grid background
    this.drawGrid(ctx, width, height, 'simple');

    if (waves.length === 0 && (!isUploadedAudioPlaying || !uploadedAudioData)) {
      return;
    }

    let displayWaves: Array<{ 
      freq: number; 
      amp: number; 
      phase: number; 
      color: string; 
      waveform?: string;
      channel?: string;
    }> = [];

    // If music is playing, parse audio data
    if (isUploadedAudioPlaying && uploadedAudioData && uploadedAudioData.length > 0) {
      displayWaves = this.audioParser.parseAudioData(Array.from(uploadedAudioData));
    } else {
      // Show configured waves
      displayWaves = waves.filter(w => !w.muted).map(w => ({
        freq: w.freq,
        amp: w.amp,
        phase: w.phase,
        color: this.getWaveColor(w, colorMode),
        waveform: w.waveform,
        channel: w.channel
      }));
    }

    if (displayWaves.length === 0) return;

    // Draw waveforms vertically stacked
    const waveHeight = Math.max(40, height / displayWaves.length);
    const padding = 8;

    displayWaves.forEach((wave, idx) => {
      const yOffset = idx * waveHeight;
      const waveY = yOffset + waveHeight / 2;
      
      this.drawWaveVertical(
        ctx, 
        wave, 
        width, 
        waveHeight, 
        yOffset,
        waveY,
        speed, 
        time, 
        colorMode
      );
    });
  }

  private drawWaveVertical(
    ctx: CanvasRenderingContext2D,
    wave: any,
    width: number,
    waveHeight: number,
    yOffset: number,
    waveY: number,
    speed: number,
    time: number,
    colorMode: 'wave' | 'channel' = 'wave'
  ): void {
    const padding = 8;
    const waveColor = this.getWaveColor(wave, colorMode);
    
    // Draw background for this wave
    ctx.fillStyle = 'rgba(255, 255, 255, 0.02)';
    ctx.fillRect(0, yOffset, width, waveHeight);
    
    // Draw separator line
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, yOffset + waveHeight);
    ctx.lineTo(width, yOffset + waveHeight);
    ctx.stroke();
    
    // Draw frequency badge
    ctx.fillStyle = waveColor;
    ctx.font = 'bold 11px monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    
    const freqText = `${wave.freq.toFixed(1)}Hz`;
    ctx.fillText(freqText, padding, waveY);
    
    // Draw waveform
    ctx.beginPath();
    ctx.strokeStyle = waveColor;
    ctx.lineWidth = 1.5;
    ctx.lineJoin = 'round';

    const baselineAmp = wave.amp / 100;
    const scale = (waveHeight / 2 - padding) * Math.min(1, baselineAmp);
    const waveStartX = 60; // Leave room for frequency badge

    for (let x = waveStartX; x < width; x += 1) {
      const t = (x - waveStartX) / (width - waveStartX);
      const phaseRad = (wave.phase * Math.PI) / 180;
      const effectiveFreq = wave.freq * speed;
      const angle = 2 * Math.PI * effectiveFreq * t + phaseRad + (time * 2);
      const yVal = scale * this.sampleWaveform(wave.waveform, angle);

      if (x === waveStartX) ctx.moveTo(x, waveY - yVal);
      else ctx.lineTo(x, waveY - yVal);
    }

    ctx.stroke();
    
    // Draw amplitude indicator
    ctx.fillStyle = waveColor;
    ctx.globalAlpha = 0.3;
    ctx.fillRect(waveStartX, waveY - scale, width - waveStartX, scale * 2);
    ctx.globalAlpha = 1;
  }

}
